import { IChannel } from '../channels/types';
import { detectCountryFromTimezone } from './detect-country-from-timezone';

/**
 * Detects the best country code for checkout creation
 *
 * Priority:
 * 1. Server-detected country from CloudFront headers (via /api/detect-country endpoint)
 * 2. Client-side timezone detection
 * 3. Channel default country
 *
 * Validates detected country against channel's supported countries
 */
export async function detectCountryForCheckout(channel: IChannel): Promise<string> {
  // Try server-side detection first (CloudFront headers)
  try {
    const response = await fetch('/api/detect-country');
    if (response.ok) {
      const data = await response.json() as { country: string | null };
      if (data.country && isCountrySupported(data.country, channel)) {
        return data.country;
      }
    }
  } catch {
    // Server detection failed, continue to client-side fallback
  }

  // Try client-side timezone detection
  const timezoneCountry = detectCountryFromTimezone();
  if (timezoneCountry && isCountrySupported(timezoneCountry, channel)) {
    return timezoneCountry;
  }

  // Fallback to channel default
  return channel.defaultCountry;
}

/**
 * Check if country is supported by the channel
 */
function isCountrySupported(countryCode: string, channel: IChannel): boolean {
  if (!channel.supportedCountries || channel.supportedCountries.length === 0) {
    return countryCode === channel.defaultCountry;
  }

  return channel.supportedCountries.includes(countryCode as any);
}
